import { useCallback } from 'react';
import { backendClient } from '@/lib/api-client';
import type { HomeResumeItem } from './useHomeDashboard';
import { useHomePagedList } from './useHomePagedList';

export type HomeResumeCursor = {
  updatedAt: string;
  itemGuid: string;
} | null;

export type HomeResumeItemsResponse = {
  items: HomeResumeItem[];
  has_more: boolean;
  next_updated_at: string | null;
  next_item_guid: string | null;
};

const PAGE_SIZE = 50;

export function homeResumeItemsRequest(
  timezone: string,
  cursor: HomeResumeCursor,
  includeDrafts: boolean,
  limit = PAGE_SIZE,
) {
  return {
    key: [
      '/home/resume-items',
      timezone,
      includeDrafts,
      cursor ? cursor.updatedAt : null,
      cursor ? cursor.itemGuid : null,
      limit,
    ] as const,
    params: {
      timezone,
      limit,
      include_drafts: includeDrafts,
      ...(cursor
        ? {
            cursor_updated_at: cursor.updatedAt,
            cursor_item_guid: cursor.itemGuid,
          }
        : {}),
    },
  };
}

export function useHomeResumeItems({
  timezone,
  includeDrafts,
  enabled = true,
}: {
  timezone: string;
  includeDrafts: boolean;
  enabled?: boolean;
}) {
  const fetchPage = useCallback(
    async (cursor: HomeResumeCursor) => {
      const request = homeResumeItemsRequest(timezone, cursor, includeDrafts);
      const response = await backendClient.get<HomeResumeItemsResponse>('/home/resume-items', {
        params: request.params,
      });
      const nextCursor =
        response.data.next_updated_at && response.data.next_item_guid
          ? {
              updatedAt: response.data.next_updated_at,
              itemGuid: response.data.next_item_guid,
            }
          : null;
      return {
        items: response.data.items,
        has_more: response.data.has_more,
        nextCursor,
      };
    },
    [includeDrafts, timezone],
  );

  return useHomePagedList({
    enabled,
    swrKey: ['/home/resume-items', timezone, includeDrafts],
    initialCursor: null,
    fetchPage,
    loadMoreErrorMessage: "Couldn't load more conversations.",
  });
}
